import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Landmark,
  IndianRupee,
  AlertTriangle,
  Wallet,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import { fintechApi } from "../services/api";
import { usePermissions } from "../context/PermissionContext";
import { useTheme } from "../context/ThemeContext";
import { MODULES, isModuleEnabled } from "../config/modules";

// ============================================================
// 🏦 Fintech Overview — main Dashboard ka chhota sa widget
// ------------------------------------------------------------
// Sirf tab dikhta hai jab:
//   • fintech_lending module on ho (config/modules.js), AUR
//   • user ke paas FINTECH_DASHBOARD_VIEW permission ho
//
// Data /fintech/dashboard/summary se aata hai. API na chale to
// saaf error dikhta hai — fake numbers kabhi nahi.
// ============================================================

const FINTECH_ON = isModuleEnabled(MODULES.fintech_lending);

const formatINR = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(val)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(val));
};

const formatCount = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(val)) return "—";
  return new Intl.NumberFormat("en-IN").format(Number(val));
};

const FintechOverviewWidget = () => {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const { currentTheme } = useTheme();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canSee = FINTECH_ON && has("FINTECH_DASHBOARD_VIEW");

  const load = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fintechApi.getFintechSummary();
      setSummary(res?.data || res || null);
    } catch (err) {
      setError(err?.message || "Fintech summary load nahi ho paaya");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [canSee]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canSee) return null;

  const cards = [
    {
      label: "Active Loans",
      value: formatCount(summary?.activeLoans ?? summary?.activeLoanCount),
      icon: Landmark,
      tone: "text-[#2e856e] bg-emerald-50",
    },
    {
      label: "Disbursed Today",
      value: formatINR(summary?.disbursedToday ?? summary?.todayDisbursed),
      icon: IndianRupee,
      tone: "text-purple-700 bg-purple-50",
    },
    {
      label: "Overdue",
      value: formatINR(summary?.totalOverdue ?? summary?.overdueAmount),
      icon: AlertTriangle,
      tone: "text-red-700 bg-red-50",
    },
    {
      label: "Collection Today",
      value: formatINR(summary?.collectedToday ?? summary?.todayCollection),
      icon: Wallet,
      tone: "text-blue-700 bg-blue-50",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-5 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Landmark className="w-5 h-5" style={{ color: currentTheme.color }} />
          <h3 className="text-lg font-bold text-gray-800">Fintech Overview</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={() => navigate("/fintech/dashboard")}
            className="flex items-center gap-1 text-sm font-bold text-[#2e856e] hover:underline"
          >
            View all
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error — fake data dikhane se behtar hai saaf batana */}
      {error && !loading && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm text-red-700 font-medium">{error}</p>
          <button
            onClick={load}
            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#2e856e] text-white"
          >
            Retry
          </button>
        </div>
      )}

      {/* Cards */}
      {!error && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className="border border-[#e5e7eb] rounded-xl p-4 flex items-center gap-3"
              >
                <div className={`p-2 rounded-lg ${card.tone}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase text-gray-400 truncate">
                    {card.label}
                  </p>
                  {loading ? (
                    <div className="h-6 w-20 bg-gray-100 rounded animate-pulse mt-1" />
                  ) : (
                    <p className="text-lg font-black text-gray-800 truncate">
                      {card.value}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FintechOverviewWidget;
